import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useApi, errMsg } from "@/lib/api";

const EMPTY = { title: "", excerpt: "", content: "", category: "Tips", cover: "", published: true };

const Editor = ({ post, onDone }) => {
  const api = useApi();
  const [f, setF] = useState(post.id ? { title: post.title, excerpt: post.excerpt, content: post.content, category: post.category, cover: post.cover, published: post.published } : EMPTY);
  const save = async () => {
    try { await api(post.id ? "put" : "post", post.id ? `/admin/posts/${post.id}` : "/admin/posts", f); toast.success("Article saved"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-3xl" data-testid="admin-post-editor">
      <DialogHeader><DialogTitle className="font-display text-2xl">{post.id ? "Edit article" : "New article"}</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <Input placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="admin-post-title" />
        <div className="grid gap-3 sm:grid-cols-2">
          <Input placeholder="Category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="admin-post-category" />
          <Input placeholder="Cover image URL (optional)" value={f.cover} onChange={(e) => setF({ ...f, cover: e.target.value })} data-testid="admin-post-cover" />
        </div>
        <Input placeholder="Short summary" value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} data-testid="admin-post-excerpt" />
        <Textarea rows={10} placeholder="Write the article. Leave an empty line between paragraphs." value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} data-testid="admin-post-content" />
        <label className="flex items-center gap-3 text-sm font-semibold"><Switch checked={f.published} onCheckedChange={(published) => setF({ ...f, published })} data-testid="admin-post-published" />Published</label>
      </div>
      <button onClick={save} className="mt-2 w-full rounded-full bg-teal-600 py-3 font-bold text-white" data-testid="admin-post-save">Save article</button>
    </DialogContent>
  );
};

export const AdminBlog = () => {
  const api = useApi();
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api("get", "/admin/posts").then(setItems).catch(() => {}), [api]);
  useEffect(() => { load(); }, [load]);
  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try { await api("delete", `/admin/posts/${p.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4">
      <button onClick={() => setEdit({})} data-testid="admin-new-post" className="inline-flex items-center gap-2 rounded-full bg-[#ff6b6b] px-5 py-2.5 text-sm font-bold text-white"><Plus className="h-4 w-4" />New article</button>
      <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white" data-testid="admin-posts-list">
        {items.map((p) => (
          <div key={p.id} className="flex items-center gap-4 p-4" data-testid="admin-post-row">
            {p.cover ? <img src={p.cover} alt="" className="h-14 w-20 rounded-lg object-cover" /> : <div className="h-14 w-20 rounded-lg bg-slate-100" />}
            <div className="flex-1"><p className="font-semibold text-slate-800">{p.title}</p><p className="text-xs text-slate-500">{p.category} · {p.published ? "Published" : "Draft"}</p></div>
            <button onClick={() => setEdit(p)} data-testid="admin-post-edit" aria-label="Edit"><Pencil className="h-4 w-4 text-slate-600" /></button>
            <button onClick={() => del(p)} data-testid="admin-post-delete" aria-label="Delete"><Trash2 className="h-4 w-4 text-rose-600" /></button>
          </div>
        ))}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        {edit && <Editor post={edit} onDone={() => { setEdit(null); load(); }} />}
      </Dialog>
    </div>
  );
};
